import React from "react";
import { observer } from "mobx-react-lite";
import { Drawer, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { XMarkIcon, ChartBarIcon, ArrowPathIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { GeopolTransformerStore } from "../geopol-transformer-store";
import { FieldStatistics, StatDistributionItem } from "../types";

interface ShadowStatsDrawerProps {
  store: GeopolTransformerStore;
}

// Field display labels
const FIELD_LABELS: Record<string, string> = {
  gender_gender_select: "Gender",
  age_range_age_select: "Age Range",
  metro_city_city_select: "Metro City",
  political_preference_preference_select: "Political Preference",
  professional_background_background_select: "Professional Background",
  region: "Region",
};

// Field order for display
const FIELD_ORDER = [
  "gender_gender_select",
  "age_range_age_select",
  "professional_background_background_select",
  "metro_city_city_select",
  "political_preference_preference_select",
  "region",
];

// Color themes for each field
const FIELD_COLORS: Record<string, { bg: string; bar: string; text: string; hover: string }> = {
  gender_gender_select: { bg: "bg-pink-50", bar: "bg-pink-500", text: "text-pink-700", hover: "hover:bg-pink-100" },
  age_range_age_select: { bg: "bg-blue-50", bar: "bg-blue-500", text: "text-blue-700", hover: "hover:bg-blue-100" },
  metro_city_city_select: { bg: "bg-emerald-50", bar: "bg-emerald-500", text: "text-emerald-700", hover: "hover:bg-emerald-100" },
  political_preference_preference_select: { bg: "bg-amber-50", bar: "bg-amber-500", text: "text-amber-700", hover: "hover:bg-amber-100" },
  professional_background_background_select: { bg: "bg-violet-50", bar: "bg-violet-500", text: "text-violet-700", hover: "hover:bg-violet-100" },
  region: { bg: "bg-cyan-50", bar: "bg-cyan-500", text: "text-cyan-700", hover: "hover:bg-cyan-100" },
};

interface StatsSectionProps {
  fieldKey: string;
  stats: FieldStatistics;
}

const StatsSection: React.FC<StatsSectionProps> = ({ fieldKey, stats }) => {
  const colors = FIELD_COLORS[fieldKey] || FIELD_COLORS.region;
  const label = FIELD_LABELS[fieldKey] || fieldKey;
  const maxCount = Math.max(...stats.distribution.map(d => d.count), 1);

  return (
    <div className={`rounded-xl p-4 ${colors.bg} border border-white/50`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className={`font-semibold text-sm ${colors.text}`}>{label}</h4>
        <span className="text-xs text-gray-500 font-medium">
          {stats.unique_values} values • {stats.total_responses} responses
        </span>
      </div>
      <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
        {stats.distribution.map((item: StatDistributionItem, idx: number) => (
          <div
            key={idx}
            className="w-full p-2 rounded-lg"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-800 truncate pr-2">
                {item.value}
              </span>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-xs font-bold text-gray-700">{item.count}</span>
                <span className="text-[10px] text-gray-500 w-12 text-right">
                  {item.percentage}%
                </span>
              </div>
            </div>
            {/* Progress bar */}
            <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
              <div
                className={`h-full ${colors.bar} rounded-full transition-all duration-300`}
                style={{ width: `${(item.count / maxCount) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const ShadowStatsDrawer: React.FC<ShadowStatsDrawerProps> = observer(({ store }) => {
  const handleClose = () => {
    store.setShowShadowStatsDrawer(false);
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={store.showShadowStatsDrawer}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          maxWidth: "100vw",
          backgroundColor: "#f8fafc",
        },
      }}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-violet-600 to-purple-700 px-5 py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Shadow Index Statistics</h2>
              <p className="text-xs text-white/70">
                Respondent profile distribution from shadow index
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Refresh/Reindex Button */}
            <button
              onClick={() => store.setShowReindexConfirmModal(true)}
              disabled={store.reindexing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-xs font-medium"
              title="Rebuild Shadow Index"
            >
              <ArrowPathIcon className={`h-4 w-4 ${store.reindexing ? 'animate-spin' : ''}`} />
              {store.reindexing ? 'Reindexing...' : 'Refresh'}
            </button>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        {store.loadingShadowStats ? (
          <div className="flex flex-col items-center justify-center py-20">
            <CircularProgress size={40} sx={{ color: "#7c3aed" }} />
            <p className="mt-4 text-sm text-gray-500">Loading statistics...</p>
          </div>
        ) : store.shadowIndexStats ? (
          <div className="space-y-4">
            {/* Summary Header */}
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                    Total Documents
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {store.shadowIndexStats.total_documents.toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                    Index
                  </p>
                  <p className="text-sm font-mono text-gray-600">
                    {store.shadowIndexStats.index}
                  </p>
                </div>
              </div>
            </div>

            {/* Stats Sections */}
            <div className="grid grid-cols-1 gap-4">
              {FIELD_ORDER.map((fieldKey) => {
                const stats = store.shadowIndexStats?.profile_statistics[
                  fieldKey as keyof typeof store.shadowIndexStats.profile_statistics
                ];
                if (!stats) return null;
                return (
                  <StatsSection
                    key={fieldKey}
                    fieldKey={fieldKey}
                    stats={stats}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <ChartBarIcon className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No statistics available</p>
            <p className="text-sm text-gray-400 mt-1">
              Unable to load shadow index data
            </p>
            <button
              onClick={() => store.fetchShadowIndexStats()}
              className="mt-4 px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 transition-colors"
            >
              Retry
            </button>
          </div>
        )}
      </div>

    </Drawer>

      {/* Reindex Confirmation Modal */}
      <Dialog
        open={store.showReindexConfirmModal}
        onClose={() => store.setShowReindexConfirmModal(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            maxWidth: "400px",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
            </div>
            <span className="text-lg font-semibold text-gray-900">
              Rebuild Shadow Index?
            </span>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              This operation will rebuild the entire shadow index from scratch. 
              It requires significant server resources and may take several minutes to complete.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-800 font-medium text-xs">
                ⚠️ Please don't run this too often. Only reindex when you've made 
                significant changes to the row transformers or need to sync the shadow index.
              </p>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={() => store.setShowReindexConfirmModal(false)}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              color: "#6b7280",
              borderColor: "#d1d5db",
              "&:hover": {
                borderColor: "#9ca3af",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => store.reindexShadowIndex()}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              backgroundColor: "#7c3aed",
              "&:hover": {
                backgroundColor: "#6d28d9",
              },
            }}
          >
            Reindex
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

